import React, { useState } from 'react';

const VonScalperFeatures = () => {
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      id: 0,
      icon: "🧠",
      title: "Tri-Timeframe Intelligence",
      subtitle: "15M/5M/1M Convergence Analysis",
      description: "Sophisticated cross-timeframe signal correlation with real-time bias detection and market structure recognition.",
      details: [
        "Real-time bias detection with confidence scoring",
        "Market structure shift identification (CHoCH)",
        "Signal confluence validation across timeframes",
        "Dynamic directional filtering"
      ],
      color: "from-cyan-600 to-blue-600"
    },
    {
      id: 1,
      icon: "🎯",
      title: "ICT Pattern Recognition",
      subtitle: "10+ Institutional Patterns",
      description: "Advanced detection of Fair Value Gaps, Order Blocks, Breaker Blocks, SMT Divergence, and liquidity zones.",
      details: [
        "Fair Value Gap (FVG) detection",
        "Order Block identification",
        "Breaker Block analysis",
        "SMT Divergence recognition",
        "DR Liquidity zone mapping"
      ],
      color: "from-purple-600 to-indigo-600"
    },
    {
      id: 2,
      icon: "🔄",
      title: "Adaptive ML Framework",
      subtitle: "Self-Calibrating Intelligence",
      description: "Machine learning system that adapts to market conditions with performance-based parameter optimization.",
      details: [
        "Market regime classification (6 states)",
        "Performance-based optimization",
        "Percentile-based calibration",
        "Conservative/Moderate/Aggressive modes"
      ],
      color: "from-indigo-600 to-purple-600"
    },
    {
      id: 3,
      icon: "🛡️",
      title: "Advanced Risk Management",
      subtitle: "Multi-Layer Protection",
      description: "Comprehensive risk controls with dynamic position sizing, equity protection, and intelligent stop management.",
      details: [
        "Daily loss caps & equity locks",
        "Position limits (separate BUY/SELL)",
        "Dynamic breakeven management",
        "Adaptive trailing stop system"
      ],
      color: "from-purple-600 to-pink-600"
    },
    {
      id: 4,
      icon: "🌐",
      title: "Multi-Market Support",
      subtitle: "Universal Compatibility",
      description: "Optimized performance across Forex, Cryptocurrency, and V75 Synthetic markets with auto-detection.",
      details: [
        "Forex majors & minors optimization",
        "24/7 cryptocurrency trading",
        "V75 synthetic indices support",
        "Auto-market detection & adaptation"
      ],
      color: "from-pink-600 to-red-600"
    },
    {
      id: 5,
      icon: "📱",
      title: "Professional Infrastructure",
      subtitle: "Bloomberg-Style Dashboard",
      description: "Real-time monitoring with Telegram integration, copy trading, and comprehensive performance analytics.",
      details: [
        "Real-time trade notifications",
        "Copy trading infrastructure",
        "System health monitoring",
        "Performance attribution analysis"
      ],
      color: "from-red-600 to-orange-600"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-slate-50 via-white to-purple-50" id="features">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center justify-center mb-6">
            <div className="h-0.5 w-12 bg-gradient-to-r from-cyan-500 to-purple-500 mr-4"></div>
            <span className="py-2 px-4 rounded-full text-purple-700 bg-purple-100 font-semibold text-sm border border-purple-200">ADVANCED FEATURES</span>
            <div className="h-0.5 w-12 bg-gradient-to-r from-purple-500 to-cyan-500 ml-4"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-slate-800 to-purple-800 bg-clip-text text-transparent">
            Institutional-Grade Technology
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            VON SCALPER combines sophisticated algorithms with professional trading strategies 
            for consistent performance across all market conditions.
          </p>
        </div>

        {/* Interactive Features Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Feature List */}
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div
                key={feature.id}
                className={`cursor-pointer transition-all duration-300 ${
                  activeFeature === index
                    ? 'bg-white shadow-xl border-2 border-purple-200'
                    : 'bg-white/50 hover:bg-white hover:shadow-lg border border-slate-200'
                } rounded-2xl p-6`}
                onClick={() => setActiveFeature(index)}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-2xl shadow-lg`}>
                    {feature.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-800 mb-1">{feature.title}</h3>
                    <p className="text-purple-600 font-medium text-sm mb-2">{feature.subtitle}</p>
                    <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                  <div className={`w-3 h-3 rounded-full transition-colors ${
                    activeFeature === index ? 'bg-purple-500' : 'bg-slate-300'
                  }`}></div>
                </div>
              </div>
            ))}
          </div>

          {/* Feature Detail */}
          <div className="lg:sticky lg:top-8">
            <div className={`bg-gradient-to-br ${features[activeFeature].color} rounded-2xl p-8 text-white shadow-2xl`}>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="text-4xl">{features[activeFeature].icon}</div>
                  <div>
                    <h3 className="text-2xl font-bold">{features[activeFeature].title}</h3>
                    <p className="text-white/80">{features[activeFeature].subtitle}</p>
                  </div>
                </div>

                <p className="text-xl text-white/90 leading-relaxed">
                  {features[activeFeature].description}
                </p>

                <div className="space-y-3">
                  <h4 className="font-semibold text-lg">Key Capabilities:</h4>
                  <ul className="space-y-2">
                    {features[activeFeature].details.map((detail, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <svg className="w-5 h-5 mt-0.5 text-white/80 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-white/90">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Progress Indicator */}
                <div className="flex space-x-2 pt-4">
                  {features.map((_, idx) => (
                    <div
                      key={idx}
                      className={`h-1 rounded-full transition-all duration-300 ${
                        idx === activeFeature ? 'bg-white flex-1' : 'bg-white/30 w-8'
                      }`}
                    ></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Features Grid */}
        <div className="mt-24 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: "⚡", title: "Sub-Second Execution", desc: "Lightning-fast signal processing" },
            { icon: "🔄", title: "24/7 Operation", desc: "Continuous market monitoring" },
            { icon: "📊", title: "Real-Time Analytics", desc: "Live performance tracking" },
            { icon: "🔐", title: "Enterprise Security", desc: "Bank-grade data protection" }
          ].map((item, idx) => (
            <div key={idx} className="bg-white rounded-xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-3">{item.icon}</div>
              <h4 className="font-bold text-slate-800 mb-2">{item.title}</h4>
              <p className="text-slate-600 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default VonScalperFeatures;